import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Form, Col, Row } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  addActionNoteAPI,
  getActionItemsAPI,
  reassignActionAPI,
  updateActionStatusAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import DrawerPanel from '../../../Components/shared/DrawerPanel';
import StatusBadge from '../../../Components/shared/StatusBadge';
import { companyUrl } from '../../../utils/mhNav';

const OWNERS = [
  { value: 'u-1', label: 'Sara Al-Hinai' },
  { value: 'u-2', label: 'Ahmed Al-Balushi' },
  { value: 'u-3', label: 'Priya Menon' },
];

export default function ActionTracker() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [note, setNote] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    ownerQuery: '',
    priority: 'all',
    dueFrom: '',
    dueTo: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getActionItemsAPI(filters);
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      { name: 'Action ID', selector: (r) => r.id, width: '120px' },
      { name: 'Description', selector: (r) => r.description, grow: 2 },
      {
        name: 'Meeting',
        cell: (r) => (
          <Link to={companyUrl(`meetings/${r.meetingId}`)}>{r.meetingTitle}</Link>
        ),
      },
      { name: 'Owner', selector: (r) => r.ownerName, width: '130px' },
      { name: 'Due', selector: (r) => r.dueDate, width: '110px' },
      {
        name: 'Status',
        width: '160px',
        cell: (r) => (
          <Form.Select
            size="sm"
            value={r.status}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              updateActionStatusAPI(r.id, e.target.value).then(() => {
                toast.success('Updated');
                load();
              });
            }}
          >
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="overdue">Overdue</option>
          </Form.Select>
        ),
      },
      {
        name: 'Priority',
        cell: (r) => <StatusBadge status={r.priority} />,
        width: '100px',
      },
      {
        name: 'Actions',
        width: '120px',
        cell: (r) => (
          <Button size="sm" variant="outline-secondary" onClick={(e) => { e.stopPropagation(); setDrawer(r); }}>
            Details
          </Button>
        ),
      },
    ],
    [load]
  );

  const conditionalRowStyles = [
    {
      when: (r) => r.status === 'overdue' || (r.dueDate && new Date(r.dueDate) < new Date() && r.status !== 'completed'),
      style: { backgroundColor: 'rgba(220, 53, 69, 0.07)' },
    },
  ];

  if (error && !rows.length) {
    return (
      <div>
        <PageHeader title="Action tracker" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Transcripts', path: companyUrl('transcripts') }, { label: 'Actions' }]} />
        <p className="text-danger">Failed to load.</p>
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Action tracker"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Transcripts', path: companyUrl('transcripts') },
          { label: 'Actions' },
        ]}
        secondaryActions={[
          {
            label: 'Export',
            onClick: () => toast.success('Export queued (mock)'),
          },
        ]}
      />
      {loading && !rows.length ? (
        <LoadingSkeleton rows={6} cols={4} />
      ) : (
        <>
          <Row className="g-2 mb-3">
            <Col md={2}>
              <Form.Label className="small">Status</Form.Label>
              <Form.Select size="sm" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
                <option value="all">All</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="overdue">Overdue</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small">Owner search</Form.Label>
              <Form.Control size="sm" value={filters.ownerQuery} onChange={(e) => setFilters((f) => ({ ...f, ownerQuery: e.target.value }))} />
            </Col>
            <Col md={2}>
              <Form.Label className="small">Priority</Form.Label>
              <Form.Select size="sm" value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))}>
                <option value="all">All</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small">Due from</Form.Label>
              <Form.Control type="date" size="sm" value={filters.dueFrom} onChange={(e) => setFilters((f) => ({ ...f, dueFrom: e.target.value }))} />
            </Col>
            <Col md={2}>
              <Form.Label className="small">Due to</Form.Label>
              <Form.Control type="date" size="sm" value={filters.dueTo} onChange={(e) => setFilters((f) => ({ ...f, dueTo: e.target.value }))} />
            </Col>
          </Row>
          <DataTableWrapper
            columns={columns}
            data={rows}
            loading={loading}
            showSearch={false}
            customTableProps={{ conditionalRowStyles }}
            onRowClick={(r) => setDrawer(r)}
          />
        </>
      )}

      <DrawerPanel isOpen={Boolean(drawer)} onClose={() => { setDrawer(null); setNote(''); }} title={drawer?.id || 'Action'} width={520}>
        {drawer && (
          <>
            <p>{drawer.description}</p>
            <div className="small text-muted mb-2">
              Linked meeting:{' '}
              <Link to={companyUrl(`meetings/${drawer.meetingId}`)}>{drawer.meetingTitle}</Link>
            </div>
            <div className="fw-semibold mb-1">Status history</div>
            <ul className="small">
              {(drawer.statusHistory || []).map((h, i) => (
                <li key={i}>{new Date(h.at).toLocaleString()} — {h.status} ({h.by})</li>
              ))}
            </ul>
            <Form.Label>Notes</Form.Label>
            <Form.Control as="textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
            <Button className="mt-2 me-2" size="sm" onClick={() => addActionNoteAPI(drawer.id, note).then(() => { toast.success('Note saved'); setNote(''); load(); setDrawer({ ...drawer, notes: [...(drawer.notes || []), { at: new Date().toISOString(), text: note }] }); })}>
              Save Note
            </Button>
            <div className="mt-3">
              <div className="small text-muted mb-1">Reassign</div>
              <Select
                options={OWNERS}
                placeholder="Select owner"
                onChange={(opt) => reassignActionAPI(drawer.id, opt.value, opt.label).then(() => { toast.success('Reassigned'); load(); })}
              />
            </div>
          </>
        )}
      </DrawerPanel>
    </div>
  );
}
